import React from "react";
import { useAuth } from "../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../services/api";
import { User, Mail, Phone, Building2, MapPin, Calendar, FileText, Loader2, AlertCircle, Shield, Download, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";

const Field = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 last:border-0" style={{ borderBottom: "1px solid var(--lm-border)" }}>
      <div className="mt-0.5 p-1.5 rounded-md" style={{ backgroundColor: "var(--lm-s2)" }}>
        <Icon className="w-3.5 h-3.5" style={{ color: "#5e6ad2" }} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--lm-ink-3)" }}>{label}</p>
        <p className="text-sm mt-0.5" style={{ color: "var(--lm-ink)" }}>{value}</p>
      </div>
    </div>
  );
};

const MonProfil = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const employeeQ = useQuery({
    queryKey: ['portal-employee-me'],
    queryFn: async () => {
      try {
        const r = await apiClient.get("v1/employees/me");
        return r.data;
      } catch (err) {
        const status = err.response?.status ?? err.originalError?.response?.status;
        if (status === 404) return null;
        throw err;
      }
    },
  });

  const employee = employeeQ.data ?? null;
  const noRecord = employeeQ.isSuccess && employeeQ.data === null;

  const formatDate = (iso) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" });
  };

  const contractLabel = (ct) => {
    const map = {
      PERMANENT: t("portal.profile.permanent"),
      TEMPORARY: t("portal.profile.fixedTerm"),
      INTERNSHIP: t("portal.profile.intern"),
      FREELANCE: t("portal.profile.freelance"),
    };
    return map[ct] ?? ct;
  };

  if (employeeQ.isPending) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#5e6ad2" }} />
      </div>
    );
  }

  if (noRecord) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-muted-foreground">
        <AlertCircle className="w-10 h-10 text-amber-400" />
        <p className="text-sm font-medium">
          {t("portal.profile.noRecord")}
        </p>
        <p className="text-xs text-gray-400">
          {t("portal.profile.contactAdmin")}
        </p>
      </div>
    );
  }

  const initials = `${employee?.first_name?.[0] ?? ""}${employee?.last_name?.[0] ?? ""}`.toUpperCase();

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-5">
            <Avatar className="h-16 w-16" style={{ boxShadow: "0 0 0 2px rgba(94,106,210,0.30)" }}>
              <AvatarImage src={employee?.picture_url} alt={employee ? `${employee.first_name ?? ""} ${employee.last_name ?? ""}`.trim() : ""} />
              <AvatarFallback className="text-white text-xl font-bold" style={{ background: "linear-gradient(135deg, #5e6ad2 0%, #828fff 100%)" }}>
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-xl font-bold text-foreground">
                {employee ? `${employee.first_name ?? ""} ${employee.last_name ?? ""}`.trim() || "—" : "—"}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">{employee?.job_title ?? employee?.job_name ?? t("portal.profile.noJob")}</p>
              <div className="flex items-center gap-2 mt-2">
                {employee?.status === "ACTIVE" && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(39,166,68,0.12)", color: "#27a644", border: "1px solid rgba(39,166,68,0.25)" }}>
                    {t("portal.profile.active")}
                  </span>
                )}
                {employee?.contract_type && (
                  <Badge variant="outline" className="text-xs">
                    {contractLabel(employee.contract_type)}
                  </Badge>
                )}
                <Badge variant="secondary" className="text-xs font-mono">
                  {employee?.registration_number}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact & assignment */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <User className="w-4 h-4" />
              {t("portal.profile.personalInfo")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Field icon={Mail}     label="Email"                                           value={employee?.email} />
            <Field icon={Phone}    label={t("portal.profile.phone")}          value={employee?.phone_number} />
            <Field icon={Calendar} label={t("portal.profile.birthDate")} value={formatDate(employee?.birth_date)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {t("portal.profile.assignment")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Field icon={Building2} label={t("portal.profile.department")}
              value={employee?.department_name ?? employee?.department?.name} />
            <Field icon={MapPin}    label={t("portal.profile.site")}
              value={employee?.site_name ?? employee?.site?.name} />
            <Field icon={FileText}  label={t("portal.profile.hireDate")} value={formatDate(employee?.hire_date)} />
          </CardContent>
        </Card>
      </div>

      {/* Gulf fields — shown only if any are present */}
      {(employee?.qid || employee?.iqama_number || employee?.gosi_number) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {t("portal.profile.identification")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Field icon={Shield} label="QID / National ID" value={employee?.qid} />
            <Field icon={Shield} label="Iqama"             value={employee?.iqama_number} />
            <Field icon={Shield} label="GOSI"              value={employee?.gosi_number} />
            <Field icon={Shield} label={t("portal.profile.workPermit")}
              value={employee?.work_permit_number
                ? `${employee.work_permit_number}${employee.work_permit_expiry ? ` (exp. ${formatDate(employee.work_permit_expiry)})` : ""}`
                : null} />
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-gray-400 text-center">
        {t("portal.profile.readOnly")}
      </p>

      {/* GDPR Article 15/20 — self-service export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="w-4 h-4" style={{ color: "#5e6ad2" }} />
            {t("portal.gdpr.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
            {t("portal.gdpr.p1")}<strong>{t("portal.gdpr.art15")}</strong>{t("portal.gdpr.p2")}<strong>{t("portal.gdpr.art20")}</strong>{t("portal.gdpr.p3")}
          </p>
          <button
            onClick={async () => {
              const token = localStorage.getItem("token");
              const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/gdpr/data-export/me`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!res.ok) {
                alert(t("portal.gdpr.exportError"));
                return;
              }
              const blob = await res.blob();
              const cd = res.headers.get("Content-Disposition") || "";
              const m = /filename="([^"]+)"/.exec(cd);
              const fname = m ? m[1] : `mawered_export_${new Date().toISOString().slice(0, 10)}.zip`;
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = fname;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
            style={{ backgroundColor: "#5e6ad2" }}
          >
            <Download className="w-4 h-4" /> {t("portal.gdpr.download")}
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MonProfil;
